import { useContext, useEffect, useState } from "react";
import { UsinasContext } from "../context/UsinasContextProvider";
import * as yup from "yup";
import { monthsSchema } from "./PVSystForm";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Indicators } from "../types";
import { getDifferentIndicatorsData } from "../usinaComparer";
import React from "react";
import { RopHighlightSingleValueInput } from "../../../main/components/RopHighlightSingleValueInput";
import { RopMonthValuesForm } from "../../../main/components/RopMonthValuesForm";
import { Grid } from "@mui/material";
export const schema = yup.object().shape({
  modulesArea: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  efficiency: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  variance: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  kwPeak: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  totalPower: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  opticalLossesPercentage: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  conversionLossesPercentage: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  inverterLossesPercentage: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  transformerLossesPercentage: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  pr: monthsSchema.optional().nullable(),
  predictedEnergy: monthsSchema.optional().nullable(),
  irradiance: monthsSchema.optional().nullable(),
  globInc: monthsSchema.optional().nullable(),
  pvSystExpectedOpticalLoss: monthsSchema.optional().nullable(),
  pvSystExpectedConversionLoss: monthsSchema.optional().nullable(),
  pvSystExpectedInverterLoss: monthsSchema.optional().nullable(),
  pvSystExpectedTransformerLoss: monthsSchema.optional().nullable(),
  pvSystExpectedSystem: monthsSchema.optional().nullable(),
  pvSystExpectedContractual: monthsSchema.optional().nullable(),
  pvSystExpectedInverter: monthsSchema.optional().nullable(),
  megaWattValue: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
});
export type UpdateIndicatorsData = yup.InferType<typeof schema>;
export const inputSx = {
  width: "100%",
  maxWidth: "200px",
};
export const IndicatorsDataForm = React.forwardRef(
  (
    props: {
      setNewIndicators: (indicators: Indicators) => void;
      setOpenIndicatorsEditModal: (open: boolean) => void;
      open: boolean;
      setCanEdit: (canEdit: boolean) => void;
    },
    ref
  ) => {
    const {
      control,
      formState: { errors },
      setValue,
      handleSubmit,
      watch,
    } = useForm({
      resolver: yupResolver(schema),
    });
    const { usina } = useContext(UsinasContext);
    const [indicators, setIndicators] = useState<Indicators | undefined>(usina?.indicators)

    useEffect(() => {
      setIndicators(usina?.indicators)
      onMount()
    }, [usina])

    const { setNewIndicators, setOpenIndicatorsEditModal, open } = props;
    const data = watch();
    useEffect(() => {
      const dataString = JSON.stringify(data);
      const indicatorsString = JSON.stringify(indicators);

      if (
        dataString !== indicatorsString &&
        getDifferentIndicatorsData(indicators, data)
      ) {
        props.setCanEdit(true);
      } else {
        props.setCanEdit(false);
      }
    }, [data, indicators]);
    const onMount = () => {
      if (!indicators) {
        setValue("modulesArea", null);
        setValue("efficiency", null);
        setValue("variance", null);
        setValue("kwPeak", null);
        setValue("totalPower", null);
        setValue("opticalLossesPercentage", null);
        setValue("conversionLossesPercentage", null);
        setValue("inverterLossesPercentage", null);
        setValue("transformerLossesPercentage", null);
        setValue("megaWattValue", null);
        setValue("pr", null);
        setValue("predictedEnergy", null);
        setValue("irradiance", null);
        setValue("globInc", null);
        setValue("pvSystExpectedOpticalLoss", null);
        setValue("pvSystExpectedConversionLoss", null);
        setValue("pvSystExpectedInverterLoss", null);
        setValue("pvSystExpectedTransformerLoss", null);
        setValue("pvSystExpectedSystem", null);
        setValue("pvSystExpectedContractual", null);
        setValue("pvSystExpectedInverter", null);
        return;
      }
      setValue("modulesArea", indicators.modulesArea);
      setValue("efficiency", indicators.efficiency);
      setValue("variance", indicators.variance);
      setValue("kwPeak", indicators.kwPeak);
      setValue("totalPower", indicators.totalPower);
      setValue("opticalLossesPercentage", indicators.opticalLossesPercentage);
      setValue("conversionLossesPercentage", indicators.conversionLossesPercentage);
      setValue("inverterLossesPercentage", indicators.inverterLossesPercentage);
      setValue("transformerLossesPercentage", indicators.transformerLossesPercentage);
      setValue("megaWattValue", indicators.megaWattValue);
      setValue("pr", indicators.pr);
      setValue("predictedEnergy", indicators.predictedEnergy);
      setValue("irradiance", indicators.irradiance);
      setValue("globInc", indicators.globInc);
      setValue("pvSystExpectedOpticalLoss", indicators.pvSystExpectedOpticalLoss);
      setValue("pvSystExpectedConversionLoss", indicators.pvSystExpectedConversionLoss);
      setValue("pvSystExpectedInverterLoss", indicators.pvSystExpectedInverterLoss);
      setValue("pvSystExpectedTransformerLoss", indicators.pvSystExpectedTransformerLoss);
      setValue("pvSystExpectedSystem", indicators.pvSystExpectedSystem);
      setValue("pvSystExpectedContractual", indicators.pvSystExpectedContractual);
      setValue("pvSystExpectedInverter", indicators.pvSystExpectedInverter);
    };
    useEffect(() => {
      if (open) onMount();
    }, [open]);
    const onSubmit = (data: UpdateIndicatorsData) => {
      setNewIndicators(data);
      setOpenIndicatorsEditModal(true);
    };
    React.useImperativeHandle(ref, () => ({
      async submitForm() {
        await handleSubmit(onSubmit)();
      },
    }));
    return (
      <Grid
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          overflowY: "auto",
        }}
        display={"flex"}
        spacing={2}
        container
      >
        <RopMonthValuesForm
          control={control}
          errors={errors}
          title="Irradiância"
          fieldName="irradiance"
        />
        <RopMonthValuesForm
          control={control}
          errors={errors}
          title="GlobInc (kWh/m²)"
          fieldName="globInc"
        />
        <RopMonthValuesForm
          control={control}
          errors={errors}
          title="PR"
          fieldName="pr"
        />
        <RopMonthValuesForm
          control={control}
          errors={errors}
          title="Energia Prevista"
          fieldName="predictedEnergy"
        />
        <RopMonthValuesForm
          control={control}
          errors={errors}
          title="Perda Óptica Esperada (PVSyst)"
          fieldName="pvSystExpectedOpticalLoss"
        />
        <RopMonthValuesForm
          control={control}
          errors={errors}
          title="Perda de Conversão Esperada (PVSyst)"
          fieldName="pvSystExpectedConversionLoss"
        />
        <RopMonthValuesForm
          control={control}
          errors={errors}
          title="Perda do Inversor Esperada (PVSyst)"
          fieldName="pvSystExpectedInverterLoss"
        />
        <RopMonthValuesForm
          control={control}
          errors={errors}
          title="Perda do Transformador Esperada (PVSyst)"
          fieldName="pvSystExpectedTransformerLoss"
        />
        <RopMonthValuesForm
          control={control}
          errors={errors}
          title="Sistema Esperado (PVSyst)"
          fieldName="pvSystExpectedSystem"
        />
        <RopMonthValuesForm
          control={control}
          errors={errors}
          title="Contratual Esperado (PVSyst)"
          fieldName="pvSystExpectedContractual"
        />
        <RopMonthValuesForm
          control={control}
          errors={errors}
          title="Inversor Esperado (PVSyst)"
          fieldName="pvSystExpectedInverter"
        />
        <RopHighlightSingleValueInput
          control={control}
          errors={errors}
          fieldName="opticalLossesPercentage"
          label="% Perdas Ópticas"
          sx={inputSx}
        />
        <RopHighlightSingleValueInput
          control={control}
          errors={errors}
          fieldName="conversionLossesPercentage"
          label="% Perdas de Conversão"
          sx={inputSx}
        />
        <RopHighlightSingleValueInput
          control={control}
          errors={errors}
          fieldName="inverterLossesPercentage"
          label="% Perdas do Inversor"
          sx={inputSx}
        />
        <RopHighlightSingleValueInput
          control={control}
          errors={errors}
          fieldName="transformerLossesPercentage"
          label="% Perdas do Transformador"
          sx={inputSx}
        />
        <RopHighlightSingleValueInput
          control={control}
          errors={errors}
          fieldName="modulesArea"
          label="Área dos módulos"
          sx={inputSx}
        />
        <RopHighlightSingleValueInput
          control={control}
          errors={errors}
          fieldName="efficiency"
          label="Eficiência"
          sx={inputSx}
        />
        <RopHighlightSingleValueInput
          control={control}
          errors={errors}
          fieldName="variance"
          label="Variância"
          sx={inputSx}
        />
        <RopHighlightSingleValueInput
          control={control}
          errors={errors}
          fieldName="kwPeak"
          label="KW de Pico"
          sx={inputSx}
        />
        <RopHighlightSingleValueInput
          control={control}
          errors={errors}
          fieldName="totalPower"
          label="Potência Total"
          sx={inputSx}
        />
        <RopHighlightSingleValueInput
          control={control}
          errors={errors}
          fieldName="megaWattValue"
          label="Valor do MWh"
          sx={inputSx}
        />
      </Grid>
    );
  }
);
